import { useCallback, useEffect, useRef, useState } from "react";
import "@/App.css";
import { Stage } from "@/components/Stage";
import { Telemetry } from "@/components/Telemetry";
import { Download, Maximize } from "lucide-react";
import { initAudio, startAmbient, preloadVoices, beep, womp, powerUp, impact, uploadSequence, successChord, speak } from "@/lib/audioEngine";

const UPLOAD_SECONDS = 13;
const toggleFullscreen = () => (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen());
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const stamp = () => new Date().toTimeString().slice(0, 8);

export default function App() {
  const [phase, setPhase] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [countdown, setCountdown] = useState(20);
  const [flash, setFlash] = useState(0);
  const [logs, setLogs] = useState([{ t: stamp(), m: "CONTAINMENT FIELD ENGAGED" }, { t: stamp(), m: "SUBJECT: THE DARKNESS — DORMANT" }]);
  const busy = useRef(false);

  const log = (m) => setLogs((l) => [...l.slice(-10), { t: stamp(), m }]);

  useEffect(() => preloadVoices(), []);

  const run = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    initAudio();
    startAmbient();
    setPhase("init");
    log("SPACEBAR AUTHORIZATION ACCEPTED");
    beep();
    powerUp(1.4);
    await sleep(1400);
    impact(0.8);
    setFlash((f) => f + 1);
    setPhase("uploading");
    log("EXPERIMENT SPORE — THE DARKNESS PROJECT");
    await speak("intro", "Experiment Spore. The Darkness Project.");
    await sleep(400);
    log("UPLOAD CHANNEL OPEN");
    await speak("uploading", "Concept uploading");
    womp(1);
    setFlash((f) => f + 1);
    await sleep(1300);
    womp(1.2);
    setFlash((f) => f + 1);
    await sleep(1300);
    log("STREAMING CONCEPT PACKETS");
    uploadSequence(UPLOAD_SECONDS);
    const begin = performance.now();
    const marks = new Set();
    await new Promise((res) => {
      const tick = () => {
        const p = Math.min(1, (performance.now() - begin) / (UPLOAD_SECONDS * 1000));
        setProgress(p);
        [25, 50, 75].forEach((m) => {
          if (p * 100 >= m && !marks.has(m)) { marks.add(m); log(`TRANSFER ${m}% — INTEGRITY NOMINAL`); }
        });
        p < 1 ? requestAnimationFrame(tick) : res();
      };
      tick();
    });
    impact(1.1);
    setFlash((f) => f + 1);
    setPhase("complete");
    log("UPLOAD COMPLETE");
    successChord();
    setCountdown(20);
    sleep(500).then(() => speak("complete", "Upload complete. Petri dish ready.")).then(() => log("PETRI DISH READY — SPECIMEN #809-X"));
    for (let s = 20; s > 0; s--) {
      setCountdown(s);
      await sleep(1000);
    }
    log("RE-CONTAINMENT CYCLE COMPLETE");
    setProgress(0);
    setPhase("idle");
    busy.current = false;
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.code !== "Space") return;
      e.preventDefault();
      run();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [run]);

  return (
    <div className={`lab phase-${phase}`} data-testid="lab-screen">
      <div className="scanlines" />
      {flash > 0 && <div key={flash} className="flash" />}
      <header className="panel col-span-12 flex items-center justify-between px-6" data-testid="header-hud">
        <div className="flex items-center gap-4">
          <span className="status-dot" />
          <span className="font-display text-2xl font-bold tracking-[0.15em] text-[#F0F6FC] whitespace-nowrap">EXPERIMENT SPORE</span>
          <span className="overline hidden md:inline">The Darkness Project</span>
        </div>
        <div className="flex items-center gap-5">
          <span className="overline hidden md:inline">VESSEL: ARK-IX · ORBIT 4.21 AU</span>
          {!window.SPECIMEN_MP4 && (
            <a data-testid="download-offline-button" href={`${process.env.REACT_APP_BACKEND_URL}/api/download`} download="experiment-spore.html" className="fs-btn">
              <Download size={16} /> DOWNLOAD OFFLINE
            </a>
          )}
          <button data-testid="fullscreen-button" onClick={(e) => { e.currentTarget.blur(); toggleFullscreen(); }} className="fs-btn" title="Full screen">
            <Maximize size={16} /> FULL SCREEN
          </button>
        </div>
      </header>
      <main className="col-span-12 md:col-span-8 min-h-0">
        <Stage phase={phase} progress={progress} countdown={countdown} onStart={run} />
      </main>
      <aside className="hidden md:block md:col-span-4 min-h-0">
        <Telemetry phase={phase} progress={progress} logs={logs} />
      </aside>
      <footer className="panel col-span-12 flex items-center justify-between px-6 font-mono text-xs text-cyan-400/80" data-testid="footer-hud">
        <span className="hidden md:inline">BIOHAZARD CLASS: OMEGA</span>
        <span>{phase === "idle" ? "AWAITING OPERATOR INPUT — [SPACE]" : "SEQUENCE IN PROGRESS"}</span>
      </footer>
    </div>
  );
}
